import { useMutation, useQueryClient } from "@tanstack/react-query";
import { putMerchantsPublicIDNote } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { merchantKeys } from "Api/Queries/Merchants/useGetMerchantQuery";
import { merchantsListKeys } from "Api/Queries/Merchants/useGetMerchantsQuery";
import type {
  Merchant,
  EntityListOfMerchantListItemDto,
  MerchantListItemDto,
} from "Infrastructure/Api/Api";

type Parameters = { publicID: string; note: string };

type CacheContext = {
  previousMerchant?: Merchant | null;
  previousMerchantsList?: EntityListOfMerchantListItemDto | null;
};

type Props = {
  onSuccess?: (data: unknown, parameters: Parameters) => void;
  onError?: (error: unknown) => void;
};

export const useUpdateMerchantNoteMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  const updateCacheOptimistically = ({ publicID, note }: Parameters) => {
    // Update merchant detail
    queryClient.setQueryData(
      merchantKeys.byMerchantPublicId(publicID),
      (old?: Merchant) => (old ? { ...old, note } : old),
    );

    // Update merchants list
    queryClient.setQueryData(
      merchantsListKeys.all,
      (old?: EntityListOfMerchantListItemDto) => {
        if (!old?.items) return old;
        return {
          ...old,
          items: old.items.map((merchant: MerchantListItemDto) =>
            merchant.publicID === publicID ? { ...merchant, note } : merchant,
          ),
        };
      },
    );
  };

  const rollbackCache = ({ publicID }: Parameters, context?: CacheContext) => {
    if (context?.previousMerchant) {
      queryClient.setQueryData(
        merchantKeys.byMerchantPublicId(publicID),
        context.previousMerchant,
      );
    }
    if (context?.previousMerchantsList) {
      queryClient.setQueryData(
        merchantsListKeys.all,
        context.previousMerchantsList,
      );
    }
  };

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ publicID, note }: Parameters) =>
        putMerchantsPublicIDNote(note, publicID),
    ),
    onMutate: async (variables) => {
      // Cancel ongoing queries and snapshot current state
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: merchantKeys.byMerchantPublicId(variables.publicID),
        }),
        queryClient.cancelQueries({ queryKey: merchantsListKeys.all }),
      ]);

      const context: CacheContext = {
        previousMerchant: queryClient.getQueryData(
          merchantKeys.byMerchantPublicId(variables.publicID),
        ),
        previousMerchantsList: queryClient.getQueryData(merchantsListKeys.all),
      };

      updateCacheOptimistically(variables);
      return context;
    },
    onError: (err, variables, context) => {
      rollbackCache(variables, context);
      onError?.(err);
    },
    onSettled: (_data, _error, { publicID }) => {
      // Ensure cache consistency
      queryClient.invalidateQueries({
        queryKey: merchantKeys.byMerchantPublicId(publicID),
      });
      queryClient.invalidateQueries({ queryKey: merchantsListKeys.all });
    },
    onSuccess,
  });
};
